/**
 * Lookup Event Bus
 *
 * Handles automatic refresh of lookup caches when records are created/updated/deleted
 * Ensures all instances of a lookup dropdown reflect the latest data without manual refresh
 *
 * Usage:
 * - On record creation: lookupEventBus.emitRefresh('lookup_instructionType')
 * - Listen for refresh: const unsubscribe = lookupEventBus.onRefresh('lookup_instructionType', handler)
 */

type LookupRefreshListener = (lookupKey: string) => void;
type BatchRefreshListener = (lookupKeys: string[]) => void;

interface EventBusConfig {
  enableLogging?: boolean;
  batchDelay?: number; // ms to batch multiple refresh events
}

class LookupEventBus {
  private listeners: Map<string, Set<LookupRefreshListener>> = new Map();
  private batchListeners: Set<BatchRefreshListener> = new Set();
  private batchQueue: Set<string> = new Set();
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private config: EventBusConfig;

  constructor(config: EventBusConfig = {}) {
    this.config = {
      enableLogging: false,
      batchDelay: 100, // 100ms to batch events
      ...config,
    };
  }

  /**
   * Emit a single refresh event for a lookup
   * Triggers all listeners subscribed to this lookup key
   */
  emitRefresh(lookupKey: string): void {
    if (this.config.enableLogging) {
      console.log(`[LookupEventBus] Refresh emitted for: ${lookupKey}`);
    }

    // Notify direct listeners
    const listeners = this.listeners.get(lookupKey);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(lookupKey);
        } catch (error) {
          console.error(`[LookupEventBus] Error calling listener for ${lookupKey}:`, error);
        }
      });
    }

    // Add to batch queue for batch listeners
    this.batchQueue.add(lookupKey);
    this.scheduleBatchNotification();
  }

  /**
   * Emit multiple refresh events at once
   * Useful when one action affects multiple lookup types
   *
   * Example:
   * - Creating a new channel might affect: channel, channelType, connectorType
   */
  emitMultiple(lookupKeys: string[]): void {
    if (this.config.enableLogging) {
      console.log(`[LookupEventBus] Batch refresh emitted for: ${lookupKeys.join(', ')}`);
    }

    lookupKeys.forEach(key => this.emitRefresh(key));
  }

  /**
   * Emit refresh based on entity type
   * Maps entity types to all affected lookup keys
   *
   * Example:
   * - Entity created: 'InstructionType'
   * - Affected lookups: ['lookup_instructionType']
   */
  emitByEntityType(entityType: string): void {
    const lookupMapping = this.getAffectedLookupsForEntity(entityType);

    if (this.config.enableLogging) {
      console.log(
        `[LookupEventBus] Entity created: ${entityType}, refreshing: ${lookupMapping.join(', ')}`
      );
    }

    this.emitMultiple(lookupMapping);
  }

  /**
   * Subscribe to refresh events for a specific lookup
   * Returns an unsubscribe function
   *
   * Usage:
   * const unsubscribe = lookupEventBus.onRefresh('lookup_instructionType', () => {
   *   // Cache was cleared, reload dropdown if needed
   * });
   *
   * // Later, unsubscribe if component unmounts
   * unsubscribe();
   */
  onRefresh(lookupKey: string, listener: LookupRefreshListener): () => void {
    if (!this.listeners.has(lookupKey)) {
      this.listeners.set(lookupKey, new Set());
    }

    this.listeners.get(lookupKey)!.add(listener);

    if (this.config.enableLogging) {
      console.log(`[LookupEventBus] Listener registered for: ${lookupKey}`);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.get(lookupKey)?.delete(listener);
      if (this.config.enableLogging) {
        console.log(`[LookupEventBus] Listener unregistered for: ${lookupKey}`);
      }
    };
  }

  /**
   * Subscribe to batch refresh events
   * Called when multiple lookups are refreshed at once
   * Useful for coordinating updates across the application
   */
  onBatchRefresh(listener: BatchRefreshListener): () => void {
    this.batchListeners.add(listener);

    return () => {
      this.batchListeners.delete(listener);
    };
  }

  /**
   * Clear all listeners
   * Use with caution - typically only in testing
   */
  clearAllListeners(): void {
    this.listeners.clear();
    this.batchListeners.clear();

    if (this.config.enableLogging) {
      console.log('[LookupEventBus] All listeners cleared');
    }
  }

  /**
   * Get current listener count for a lookup
   * Useful for debugging
   */
  getListenerCount(lookupKey: string): number {
    return this.listeners.get(lookupKey)?.size ?? 0;
  }

  /**
   * Get all registered lookup keys
   */
  getRegisteredLookups(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Map entity types to affected lookup keys
   * Used for intelligent cache invalidation
   *
   * Example:
   * When 'Instruction' is created, update both 'lookup_instruction' and 'lookup_instructionType'
   */
  private getAffectedLookupsForEntity(entityType: string): string[] {
    const affectedMap: Record<string, string[]> = {
      // Instruction type affects instruction type lookup
      'InstructionType': ['lookup_instructionType'],
      'Instruction': ['lookup_instructionType'],

      // Channel creation affects channel and channel type lookups
      'Channel': ['lookup_channel', 'lookup_channelType'],
      'ChannelType': ['lookup_channelType'],

      // Profile affects profile lookup
      'Profile': ['lookup_profile'],

      // Agent creation affects agent, agent type, and related lookups
      'Agent': ['lookup_agent', 'lookup_agentType'],
      'AgentType': ['lookup_agentType'],

      // MCP Tool affects function lookup
      'McpTool': ['lookup_mcpTool'],

      // Function type affects function lookup
      'FunctionType': ['lookup_functionType'],

      // Connector affects multiple lookups
      'Connector': ['lookup_connector', 'lookup_connectorGroup', 'lookup_connectorType'],
      'ConnectorGroup': ['lookup_connectorGroup'],
      'ConnectorType': ['lookup_connectorType'],

      // RAG/Embedding affects knowledge base lookups
      'RagProvider': ['lookup_ragProvider'],
      'EmbeddingProvider': ['lookup_ragProvider'],
      'EmbeddingModel': ['lookup_embeddingModel'],

      // Business taxonomy
      'BusinessDomain': ['lookup_businessDomain'],
      'BusinessCategory': ['lookup_businessCategory'],
      'BusinessClassification': ['lookup_businessClassification'],
      'BusinessSector': ['lookup_businessSector'],
      'BusinessIndustry': ['lookup_businessIndustry'],
      'BusinessProfile': ['lookup_businessProfile'],

      // LLM Model
      'LlmProvider': ['lookup_llmProvider'],
      'LlmModel': ['lookup_llmModel', 'lookup_modelCategory', 'lookup_modelType'],
      'ModelCategory': ['lookup_modelCategory'],
      'ModelType': ['lookup_modelType'],

      // Assignment entities
      'Hook': ['lookup_hook'],
      'Identity': ['lookup_identity'],
      'Role': ['lookup_role'],
      'User': ['lookup_user'],
      'Team': ['lookup_team'],
    };

    return affectedMap[entityType] || [];
  }

  /**
   * Schedule batch notification
   * Batches multiple refresh events within a timeframe to reduce listener calls
   */
  private scheduleBatchNotification(): void {
    // Clear existing timeout
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    // Schedule new batch notification
    this.batchTimeout = setTimeout(() => {
      if (this.batchQueue.size > 0) {
        const batchKeys = Array.from(this.batchQueue);

        // Notify batch listeners
        this.batchListeners.forEach(listener => {
          try {
            listener(batchKeys);
          } catch (error) {
            console.error('[LookupEventBus] Error calling batch listener:', error);
          }
        });

        // Clear batch queue
        this.batchQueue.clear();
      }

      this.batchTimeout = null;
    }, this.config.batchDelay);
  }
}

/**
 * Global lookup event bus instance
 */
export const lookupEventBus = new LookupEventBus({
  enableLogging: import.meta.env?.DEV === true,
  batchDelay: 100,
});

export default lookupEventBus;
